/**
 * Configuration for application-wide feature modules.
 * Controls feature flags that can be shared across different parts of the application.
 * Each module should have a boolean flag to enable/disable the entire module functionality.
 */

/**
 * Interfaccia per la configurazione dei moduli
 */
export interface ModulesConfiguration {
	/** Indica se il modulo CrossCompanyMirroring è abilitato */
	readonly crossCompanyMirroring: boolean;
	/** Indica se il modulo Economics è abilitato */
	readonly economics: boolean;
	/** Indica se il modulo Refunds è abilitato */
	readonly refunds: boolean;
	/** Indica se il modulo BusinessCentralIntegration è abilitato */
	readonly businessCentralIntegration: boolean;
}

/**
 * Tipo per le chiavi dei moduli disponibili
 */
export type ModuleKey = keyof ModulesConfiguration;

/**
 * Costanti per i nomi delle variabili d'ambiente
 */
export const ENV_VARS = {
	CROSS_COMPANY_MIRRORING: 'ENABLE_CROSS_COMPANY_MIRRORING',
	ECONOMICS: 'ENABLE_ECONOMICS',
	REFUNDS: 'ENABLE_REFUNDS',
	BUSINESS_CENTRAL_INTEGRATION: 'ENABLE_BUSINESS_CENTRAL_INTEGRATION',
} as const;

/**
 * Array dei moduli disponibili per iterazione
 */
export const MODULE_KEYS: readonly ModuleKey[] = [
	'crossCompanyMirroring',
	'economics',
	'refunds',
	'businessCentralIntegration',
] as const;

/**
 * Configurazione dei moduli con getter che leggono le variabili d'ambiente
 */
const modulesConfiguration: ModulesConfiguration = {
	/**
	 * Gets whether the CrossCompanyMirroring module is enabled.
	 * @returns true if CrossCompanyMirroring features should be active; otherwise, false.
	 */
	get crossCompanyMirroring(): boolean {
		return process.env.ENABLE_CROSS_COMPANY_MIRRORING === 'true';
	},

	/**
	 * Gets whether the Economics module is enabled.
	 * @returns true if Economics features should be active; otherwise, false.
	 */
	get economics(): boolean {
		return process.env.ENABLE_ECONOMICS === 'true';
	},

	/**
	 * Gets whether the Refunds module is enabled.
	 * @returns true if Refunds features should be active; otherwise, false.
	 */
	get refunds(): boolean {
		return process.env.ENABLE_REFUNDS === 'true';
	},

	/**
	 * Gets whether the BusinessCentralIntegration module is enabled.
	 * @returns true if BusinessCentralIntegration features should be active; otherwise, false.
	 */
	get businessCentralIntegration(): boolean {
		return process.env.ENABLE_BUSINESS_CENTRAL_INTEGRATION === 'true';
	},
};

/**
 * Export di default per compatibilità con il codice esistente
 */
export default modulesConfiguration;

/**
 * Funzioni di utility per lavorare con la configurazione dei moduli
 */

/**
 * Verifica se un modulo specifico è abilitato
 * @param moduleKey - Chiave del modulo da verificare
 * @returns true se il modulo è abilitato
 */
export function isModuleEnabled(moduleKey: ModuleKey): boolean {
	return modulesConfiguration[moduleKey];
}

/**
 * Ottiene tutti i moduli abilitati
 * @returns Array delle chiavi dei moduli abilitati
 */
export function getEnabledModules(): ModuleKey[] {
	return MODULE_KEYS.filter((moduleKey) => isModuleEnabled(moduleKey));
}

/**
 * Ottiene tutti i moduli disabilitati
 * @returns Array delle chiavi dei moduli disabilitati
 */
export function getDisabledModules(): ModuleKey[] {
	return MODULE_KEYS.filter((moduleKey) => !isModuleEnabled(moduleKey));
}

/**
 * Verifica se almeno uno dei moduli specificati è abilitato
 * @param moduleKeys - Array delle chiavi dei moduli da verificare
 * @returns true se almeno un modulo è abilitato
 */
export function isAnyModuleEnabled(moduleKeys: readonly ModuleKey[]): boolean {
	return moduleKeys.some((moduleKey) => isModuleEnabled(moduleKey));
}

/**
 * Verifica se tutti i moduli specificati sono abilitati
 * @param moduleKeys - Array delle chiavi dei moduli da verificare
 * @returns true se tutti i moduli sono abilitati
 */
export function areAllModulesEnabled(moduleKeys: readonly ModuleKey[]): boolean {
	return moduleKeys.every((moduleKey) => isModuleEnabled(moduleKey));
}

/**
 * Ottiene lo stato di tutti i moduli come oggetto
 * @returns Oggetto con lo stato di ogni modulo
 */
export function getAllModulesStatus(): Record<ModuleKey, boolean> {
	return MODULE_KEYS.reduce(
		(status, moduleKey) => {
			status[moduleKey] = isModuleEnabled(moduleKey);
			return status;
		},
		{} as Record<ModuleKey, boolean>,
	);
}

/**
 * Hook per debugging - stampa lo stato di tutti i moduli
 * @param prefix - Prefisso opzionale per il log
 */
export function logModulesStatus(prefix: string = 'Modules Status'): void {
	if (process.env.NODE_ENV !== 'production') {
		const status = getAllModulesStatus();
		console.group(prefix);
		MODULE_KEYS.forEach((moduleKey) => {
			const icon = status[moduleKey] ? '✅' : '❌';
			console.log(`${icon} ${moduleKey}: ${status[moduleKey]}`);
		});
		console.groupEnd();
	}
}
